// AXIOM — FormulaBar
// The signature UI: the player's live damage expression, typeset by hand in
// HTML/CSS (no MathJax, no KaTeX, zero external assets), with drag-and-drop
// reorderable operator tokens. Order changes the math — (x+5)^2 != x^2+5 — so the
// typeset expression AND the damage preview both update live *while* dragging.
//
// This module also exports the small DOM + typesetting toolkit the rest of the UI
// layer builds on (h, svgEl, mathematical layout primitives, number formatting).

import './styles.css';
import S from '../core/Registry.js';
import { bus } from '../core/EventBus.js';
import { rand } from '../core/Rand.js';
import { t, pick, getLang, onLangChange } from './i18n.js';

/* ══════════════════════════════════════════════════════════════════════════
   DOM helpers
   ══════════════════════════════════════════════════════════════════════════ */

export function h(tag, cls, ...kids) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  add(e, kids);
  return e;
}

export function add(parent, kids) {
  for (const k of kids) {
    if (k === null || k === undefined || k === false || k === '') continue;
    if (Array.isArray(k)) { add(parent, k); continue; }
    parent.appendChild(
      (typeof k === 'string' || typeof k === 'number')
        ? document.createTextNode(String(k))
        : k,
    );
  }
  return parent;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
export function svgEl(tag, attrs = {}, ...kids) {
  const e = document.createElementNS(SVG_NS, tag);
  for (const k in attrs) e.setAttribute(k, attrs[k]);
  for (const c of kids.flat(3)) if (c) e.appendChild(c);
  return e;
}

/* ══════════════════════════════════════════════════════════════════════════
   Number formatting (BigNum-aware, but degrades to plain JS numbers)
   ══════════════════════════════════════════════════════════════════════════ */

const NAMED = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc',
  'UDc', 'DDc', 'TDc', 'QaDc', 'QiDc', 'SxDc', 'SpDc', 'OcDc', 'NoDc', 'Vg'];

/** log10 of anything we might be handed (BigNum | number | null). */
export function log10of(v) {
  if (v == null) return -Infinity;
  if (typeof v === 'number') return v > 0 ? Math.log10(v) : -Infinity;
  if (typeof v.log10 === 'function') {
    const r = v.log10();
    return typeof r === 'number' ? r : (r && typeof r.toNumber === 'function' ? r.toNumber() : -Infinity);
  }
  if (typeof v.exponent === 'number') {
    const m = v.mantissa ?? 1;
    return m > 0 ? v.exponent + Math.log10(m) : -Infinity;
  }
  if (typeof v.toNumber === 'function') { const n = v.toNumber(); return n > 0 ? Math.log10(n) : -Infinity; }
  return -Infinity;
}

/** Short display string. Prefers BigNum.format(), else replicates its tiers. */
export function fmt(v) {
  if (v == null) return '0';
  if (typeof v !== 'number') {
    if (typeof v.format === 'function') { try { return v.format('auto'); } catch { /* fall through */ } }
    if (typeof v.toString === 'function' && v.constructor !== Object) {
      const s = v.toString();
      if (s && s !== '[object Object]') return s;
    }
  }
  const lg = log10of(v);
  if (!isFinite(lg)) return lg === -Infinity ? '0' : '∞';
  const n = typeof v === 'number' ? v : (v.toNumber ? v.toNumber() : 0);
  if (lg < 6 && isFinite(n)) {
    return Math.abs(n) < 1000
      ? String(Math.round(n * 10) / 10).replace(/\.0$/, '')
      : Math.round(n).toLocaleString('en-US');
  }
  if (lg < 33) {
    const tier = Math.floor(lg / 3);
    const mant = Math.pow(10, lg - tier * 3);
    return mant.toFixed(mant < 10 ? 2 : 1) + NAMED[tier];
  }
  if (lg < 1e6) {
    const e = Math.floor(lg);
    return Math.pow(10, lg - e).toFixed(2) + 'e' + e;
  }
  // Tetration-scale: how many times you take log10 before it's small.
  let x = lg, levels = 1;
  while (x > 10 && levels < 12) { x = Math.log10(x); levels++; }
  return x.toFixed(1) + '↑↑' + levels;
}

/** Short value with a typographic minus, shared by Forge's signed pipeline. */
export function fmtSigned(v) {
  const negative = typeof v === 'number' ? v < 0 : !!v?.isNeg?.();
  if (!negative) return fmt(v);
  const magnitude = typeof v === 'number' ? Math.abs(v) : (v.abs?.() ?? v);
  return `−${fmt(magnitude).replace(/^-/, '')}`;
}

/** Order-of-magnitude string used by the death screen ("10^42"). */
export function magnitudeOf(v) {
  const lg = log10of(v);
  if (!isFinite(lg) || lg < 0) return 0;
  return Math.floor(lg);
}

/* ══════════════════════════════════════════════════════════════════════════
   Mathematical typesetting primitives
   Every one returns a DOM node. Layout is flex/grid; rules are real borders;
   the radical vinculum is a border that meets an SVG hook with a
   non-scaling stroke so it stays hairline at any height.
   ══════════════════════════════════════════════════════════════════════════ */

export const mvar = (s) => h('i', 'mm-var', s);                 // italic serif variable
export const mnum = (s) => h('span', 'mm-num', String(s));      // upright lining figures
export const mop = (s) => h('span', 'mm-op', s);                // binary operator, spaced
export const mfun = (s) => h('span', 'mm-fun', s);              // upright function name
export const mfunbin = (s) => h('span', 'mm-fun mm-fun-bin', s); // upright, spaced both sides (mod)
export const mpunct = (s) => h('span', 'mm-punct', s);

export function mrow(...kids) { return h('span', 'mm-row', kids); }

export function msup(base, exp) {
  return h('span', 'mm-sup-wrap', base, h('sup', 'mm-sup', exp));
}
export function msub(base, sub) {
  return h('span', 'mm-sup-wrap', base, h('sub', 'mm-sub', sub));
}

/** Real fraction: numerator / rule / denominator, centered, with a hairline. */
export function mfrac(num, den) {
  return h('span', 'mm-frac',
    h('span', 'mm-frac-n', num),
    h('span', 'mm-frac-bar'),
    h('span', 'mm-frac-d', den));
}

/** Radical with a drawn hook + a vinculum that spans the radicand exactly. */
export function mroot(body, index) {
  const hook = svgEl('svg', {
    class: 'mm-rad-hook', viewBox: '0 0 14 28', preserveAspectRatio: 'none',
    'aria-hidden': 'true',
  }, svgEl('path', {
    d: 'M0.6 17.2 L4.0 15.4 L7.2 26.0 L13.4 1.2',
    fill: 'none', stroke: 'currentColor', 'stroke-width': '1.35',
    'stroke-linejoin': 'miter', 'stroke-linecap': 'square',
    'vector-effect': 'non-scaling-stroke',
  }));
  return h('span', 'mm-rad',
    index ? h('span', 'mm-rad-idx', index) : null,
    hook,
    h('span', 'mm-rad-body', body));
}

/** Scaling delimiters. `kind`: paren | bracket | brace | floor | ceil | abs | angle */
export function mdelim(kind, body) {
  const el = h('span', `mm-delim mm-delim-${kind}`,
    h('span', 'mm-delim-l', kind === 'paren' ? '(' : kind === 'bracket' ? '[' : kind === 'brace' ? '{' : ''),
    h('span', 'mm-delim-body', body),
    h('span', 'mm-delim-r', kind === 'paren' ? ')' : kind === 'bracket' ? ']' : kind === 'brace' ? '}' : ''));
  if (kind === 'paren' || kind === 'bracket' || kind === 'brace') el.dataset.stretch = '1';
  return el;
}

/** Big operator (Σ ∫ ∏ lim) with optional limits above and below. */
export function mbigop(glyph, lower, upper, body, cls = '') {
  return h('span', `mm-bigop ${cls}`,
    h('span', 'mm-bigop-stack',
      upper ? h('span', 'mm-bigop-up', upper) : null,
      h('span', 'mm-bigop-glyph', glyph),
      lower ? h('span', 'mm-bigop-lo', lower) : null),
    body ? h('span', 'mm-bigop-body', body) : null);
}

/**
 * Stretch the glyph-based delimiters to match their content.
 * One measure/write pass, called after a rebuild — never per frame.
 */
export function stretchDelims(root) {
  const nodes = root.querySelectorAll('.mm-delim[data-stretch]');
  if (!nodes.length) return;
  const jobs = [];
  for (const n of nodes) {
    const body = n.querySelector(':scope > .mm-delim-body');
    const l = n.querySelector(':scope > .mm-delim-l');
    if (!body || !l) continue;
    jobs.push([n, body.getBoundingClientRect().height, l.getBoundingClientRect().height]);
  }
  for (const [n, bh, lh] of jobs) {
    if (!lh) continue;
    const k = Math.max(1, Math.min(4.5, (bh + 4) / lh));
    n.style.setProperty('--dstretch', k.toFixed(3));
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   Operator → typeset descriptors
   Each entry wraps the accumulated expression, exactly like the slot pipeline
   does numerically. `prec` drives automatic parenthesisation, which is *the*
   reason the player can see (x+7)^2 differ from x^2+7 at a glance.
   ══════════════════════════════════════════════════════════════════════════ */

const P = { ADD: 20, MOD: 15, MUL: 40, POW: 60, POST: 75, ATOM: 100, BIG: 30 };

function wrap(node, prec, need) {
  return prec < need ? { node: mdelim('paren', node), prec: P.ATOM } : { node, prec };
}
const W = (e, need) => wrap(e.node, e.prec, need).node;

const numOr = (v, d) => (v === undefined || v === null || Number.isNaN(v) ? d : v);
const fmtN = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  return Math.abs(n - Math.round(n)) < 1e-9 ? String(Math.round(n)) : n.toFixed(2);
};

/**
 * An operand that may carry a sign. Ops.sanitizeValue lets a minus reach × and
 * ÷ and nothing else, so this is the only place in the expression where a minus
 * stands where a NUMBER is expected rather than where an OPERATOR is — and
 * `x · −3`, a typeset operator glyph beside a bare hyphen, is genuinely
 * ambiguous about which of the two it is. Bracket it, use the same U+2212 the
 * operators use, and give the sign its own class: this one glyph decides
 * whether the shot lands at all, so it has to be colourable.
 */
export function mopnd(v, dflt) {
  const n = Number(numOr(v, dflt));
  if (!(n < 0)) return mnum(fmtN(n));
  return h('span', 'mm-neg', '(', h('span', 'mm-neg-sign', '−'), mnum(fmtN(-n)), ')');
}

// inner: { node, prec } — returns { node, prec }
export const TYPESET = {
  add:   (e, v) => ({ node: mrow(W(e, P.ADD), mop('+'), mnum(fmtN(numOr(v, 1)))), prec: P.ADD }),
  sub:   (e, v) => ({ node: mrow(W(e, P.ADD), mop('−'), mnum(fmtN(numOr(v, 1)))), prec: P.ADD }),
  mul:   (e, v) => ({ node: mrow(W(e, P.MUL), mop('·'), mopnd(v, 2)), prec: P.MUL }),
  div:   (e, v) => ({ node: mfrac(e.node, mopnd(v, 2)), prec: P.ATOM }),
  pow:   (e, v) => ({ node: msup(W(e, P.ATOM), mnum(fmtN(numOr(v, 2)))), prec: P.POW }),
  root:  (e) => ({ node: mroot(e.node), prec: P.ATOM }),
  log:   (e, v) => ({ node: mrow(msub(mfun('log'), mnum(fmtN(numOr(v, 2)))), mdelim('paren', e.node)), prec: P.ATOM }),
  fact:  (e) => ({ node: mrow(W(e, P.ATOM), mop('!')), prec: P.POST }),
  sigma: (e) => ({
    node: mbigop('∑', mrow(mvar('k'), mop('='), mnum(0)), mnum(4), W(e, P.MUL + 1)), prec: P.BIG,
  }),
  integral: (e) => ({
    node: mbigop('∫', mnum(0), mvar('t'), mrow(W(e, P.MUL + 1), h('span', 'mm-d', mrow(mfun('d'), mvar('t'))))), prec: P.BIG,
  }),
  deriv: (e) => ({ node: mrow(mfrac(mfun('d'), mrow(mfun('d'), mvar('t'))), W(e, P.MUL)), prec: P.MUL }),
  tetra: (e, v) => ({ node: mrow(W(e, P.ATOM), mop('↑↑'), mnum(fmtN(numOr(v, 2)))), prec: P.POW }),
  lim:   (e) => ({
    node: mbigop('lim', mrow(mvar('ε'), mop('→'), mnum(0)), null, W(e, P.MUL + 1), 'mm-bigop-lim'), prec: P.BIG,
  }),
  mod:   (e, v) => ({ node: mrow(W(e, P.MOD + 1), mfunbin('mod'), mnum(fmtN(numOr(v, 7)))), prec: P.MOD }),
  imag:  (e) => ({ node: mrow(mvar('i'), mop('·'), W(e, P.MUL)), prec: P.MUL }),
  abs:   (e) => ({ node: mdelim('abs', e.node), prec: P.ATOM }),
  sin:   (e) => ({
    node: mrow(W(e, P.MUL), mop('·'), mdelim('paren', mrow(mnum(1), mop('+'), mfun('sin'), mvar('t')))), prec: P.MUL,
  }),
  const: (e, v, tok) => ({ node: mrow(W(e, P.MUL), mop('·'), h('i', 'mm-var mm-const', constGlyph(tok, v))), prec: P.MUL }),
};
// Ops.js ships the three constants as their own op ids rather than one `const`.
TYPESET.pi = (e) => ({ node: mrow(W(e, P.MUL), mop('·'), h('i', 'mm-var mm-const', 'π')), prec: P.MUL });
TYPESET.e = (e) => ({ node: mrow(W(e, P.MUL), mop('·'), h('i', 'mm-var mm-const', 'e')), prec: P.MUL });
TYPESET.phi = (e) => ({ node: mrow(W(e, P.MUL), mop('·'), h('i', 'mm-var mm-const', 'φ')), prec: P.MUL });

function constGlyph(tok, v) {
  const id = (tok && (tok.opId || tok.constId || tok.symbol || tok.name)) || '';
  if (/phi|φ/i.test(id)) return 'φ';
  if (/^e$|euler/i.test(id)) return 'e';
  if (/pi|π/i.test(id)) return 'π';
  if (typeof v === 'number') {
    if (Math.abs(v - 1.618) < 0.02) return 'φ';
    if (Math.abs(v - 2.718) < 0.02) return 'e';
    if (Math.abs(v - 3.1416) < 0.02) return 'π';
  }
  return 'π';
}

/* Chip glyphs.
 *
 * An INFIX chip shows only what the slot contributes — `+ 7`, `· (−3)` — and no
 * leading `x`. It used to carry one, on the theory that `x · (−3)` reads as
 * maths rather than as an icon. It does not. There is exactly one `x` in the
 * expression, so three chips each claiming one is a lie about the structure;
 * and with a negative operand `x · (−3)` sits one thin middot away from reading
 * as `x − 3`, which is the opposite operation. The chip is a slot, not an
 * expression: show the operator and its operand, spaced, and it composes with
 * the bar above by simple concatenation.
 *
 * A WRAPPING chip keeps its `x`, because √, |x|, log and x! are illegible
 * without an argument to wrap. Those cannot be confused with a binary minus. */
export const CHIP_GLYPH = {
  add: (v) => mrow(mop('+'), mnum(fmtN(numOr(v, 1)))),
  sub: (v) => mrow(mop('−'), mnum(fmtN(numOr(v, 1)))),
  mul: (v) => mrow(mop('·'), mopnd(v, 2)),
  div: (v) => mrow(mop('÷'), mopnd(v, 2)),
  pow: (v) => mrow(mop('^'), mnum(fmtN(numOr(v, 2)))),
  root: () => mroot(mvar('x')),
  log: (v) => mrow(msub(mfun('log'), mnum(fmtN(numOr(v, 2)))), mvar('x')),
  fact: () => mrow(mvar('x'), mop('!')),
  sigma: () => mrow(h('span', 'mm-chipbig', '∑')),
  integral: () => mrow(h('span', 'mm-chipbig', '∫')),
  deriv: () => mfrac(mfun('d'), mrow(mfun('d'), mvar('t'))),
  tetra: (v) => mrow(mop('↑↑'), mnum(fmtN(numOr(v, 2)))),
  lim: () => mrow(mfun('lim')),
  mod: (v) => mrow(mfunbin('mod'), mnum(fmtN(numOr(v, 7)))),
  imag: () => mrow(mvar('i')),
  abs: () => mdelim('abs', mvar('x')),
  sin: () => mrow(mfun('sin'), mvar('t')),
  const: (v, tok) => mrow(mop('·'), h('i', 'mm-var mm-const', constGlyph(tok, v))),
  pi: () => mrow(mop('·'), h('i', 'mm-var mm-const', 'π')),
  e: () => mrow(mop('·'), h('i', 'mm-var mm-const', 'e')),
  phi: () => mrow(mop('·'), h('i', 'mm-var mm-const', 'φ')),
};

const TIER_ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI'];
const OP_TIER = {
  add: 1, sub: 1, mul: 2, div: 2, pow: 3, root: 3, log: 3,
  fact: 4, sigma: 4, integral: 4, deriv: 4, tetra: 5, lim: 5,
  mod: 3, imag: 3, abs: 2, sin: 3, const: 2, pi: 2, e: 2, phi: 2,
};
const OP_LABEL = {
  add: '加算', sub: '減算', mul: '乗算', div: '除算', pow: '冪', root: '平方根',
  log: '対数', fact: '階乗', sigma: '級数和', integral: '積分', deriv: '微分',
  tetra: 'テトレーション', lim: '極限', mod: '剰余', imag: '虚数', abs: '絶対値',
  sin: '正弦', const: '定数', pi: '円周率', e: '自然対数の底', phi: '黄金比',
};

/** Best-effort op metadata: prefers the live OPS table when Agent B's module
 *  is present, falls back to the local table so the UI is never blank. */
// Statically imported. This used to be a `@vite-ignore` dynamic import so the UI
// could boot before the maths module existed; now that every module ships, the
// ignore hint means Vite does NOT bundle it, so production requested a URL that
// does not exist and the SPA fallback answered with index.html at 200 — a strict
// MIME failure on every load, for a chunk that was already in the bundle.
let OPS_TABLE = null;
(async () => {
  try {
    const m = await import('../math/Ops.js');
    if (m && m.OPS) OPS_TABLE = m.OPS;
  } catch { /* Agent B hasn't landed yet — local fallback is fine. */ }
})();

export function opMeta(token) {
  const id = token && (token.opId || token.op || token.id);
  const live = OPS_TABLE && id ? OPS_TABLE[id] : null;
  return {
    id: id || null,
    tier: live?.tier ?? OP_TIER[id] ?? 1,
    // Ops.js carries label/labelJa; pick() honours the current language.
    label: (live ? pick(live, 'label') : null) || OP_LABEL[id] || id || '',
    symbol: live?.symbol ?? null,
    rate: live?.rate ?? 1,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   Expression assembly
   ══════════════════════════════════════════════════════════════════════════ */

/** Build the full typeset expression node from an ordered token list. */
export function buildExpression(tokens, opts = {}) {
  const chain = opts.chain | 0;
  let e = { node: mvar('x'), prec: P.ATOM };
  const marks = [];
  tokens.forEach((tok, i) => {
    if (!tok) return;
    const id = tok.opId || tok.op || tok.id;
    const fn = TYPESET[id];
    if (!fn) return;
    const before = e.node;
    e = fn(e, tok.value, tok);
    if (e.node !== before) marks.push([e.node, i]);
  });

  // Damage is an integer: the floor brackets are honest, and they frame the
  // whole expression the way a display equation wants to be framed.
  let body = mdelim('floor', e.node);
  if (chain > 0) body = mrow(body, mop('·'), msup(h('i', 'mm-var mm-const', 'φ'), h('span', 'mm-chain', String(chain))));

  const root = h('span', 'mm-expr',
    mvar('D'), mop('='), body);

  // tag the outermost node contributed by each slot so we can highlight on hover
  for (const [node, i] of marks) if (node.classList) node.dataset.slot = String(i);
  return root;
}

/* ══════════════════════════════════════════════════════════════════════════
   Sign parity
   |−3 · −4| == |3 · 4|, so a surviving PAIR is never worth raw damage — it is
   paid in fire rate and band width. An ODD tail is a dead shot. Neither of
   those facts is visible in a damage number, so the bar has to say them out
   loud, and it has to say them in the same breath as the number they contradict.
   ══════════════════════════════════════════════════════════════════════════ */

// Local rather than in the i18n dictionary: parity is a FormulaBar concept and
// no other screen has a word for it yet. Ordered like every other pair in the
// dictionary — the current language leads, the other one is the subtitle.
const SIGN_COPY = {
  paired: { glyph: '⊕', cls: 'is-up', ja: '符号が揃った', en: 'SIGN PAIRED' },
  odd: { glyph: '⊖', cls: 'is-down', ja: '符号が奇数', en: 'SIGN ODD' },
};

export function signCopy(key) {
  const c = SIGN_COPY[key];
  return getLang() === 'ja' ? [c.ja, c.en] : [c.en, c.ja];
}

/**
 * The yardstick a drag preview is measured against: a magnitude AND the sign
 * parity that magnitude cannot carry, because log10of() is a log of the
 * ABSOLUTE value.
 */
export function signSnapshot(res) {
  return {
    lg: log10of(res && res.damage),
    parity: (res && res.flags && res.flags.signParity) | 0,
  };
}

/**
 * What the drag preview should say about `cur` relative to `base`.
 *
 * Parity is answered before magnitude. A reorder that flips +1e6 to −1e6 has a
 * magnitude delta of exactly zero, so magnitude alone reports 「変化なし」 about
 * the single change the whole sign system exists to make visible. An odd tail
 * is also a dead shot, and no percentage is a truthful thing to print about a
 * zero — so it is answered even when the baseline was already odd.
 *
 * `text: null` with `is-flat` means "no magnitude story"; the caller supplies
 * the localised 「変化なし」.
 *
 * @param {{lg:number,parity:number}|null} base
 * @param {{lg:number,parity:number}|null} cur
 * @returns {{sign:('paired'|'odd'|null), cls:string, text:(string|null)}}
 */
export function previewVerdict(base, cur) {
  if (!base || !cur) return { sign: null, cls: 'is-flat', text: null };
  if (cur.parity) return { sign: 'odd', cls: SIGN_COPY.odd.cls, text: null };
  if (base.parity) return { sign: 'paired', cls: SIGN_COPY.paired.cls, text: null };

  const d = cur.lg - base.lg;
  if (!isFinite(d) || Math.abs(d) < 1e-6) return { sign: null, cls: 'is-flat', text: null };
  const up = d > 0;
  if (Math.abs(d) < 1) {
    const pct = (Math.pow(10, d) - 1) * 100;
    return {
      sign: null,
      cls: up ? 'is-up' : 'is-down',
      text: `${up ? '▲' : '▼'} ${Math.abs(pct).toFixed(Math.abs(pct) < 10 ? 1 : 0)}%`,
    };
  }
  return {
    sign: null,
    cls: up ? 'is-up' : 'is-down',
    text: `${up ? '▲' : '▼'} ×10${supDigits(Math.abs(d).toFixed(1))}`,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   FormulaBar
   ══════════════════════════════════════════════════════════════════════════ */

const SPRING = 'transform 300ms cubic-bezier(.22,.9,.24,1.14)';

export class FormulaBar {
  constructor(root = document.body) {
    this.root = root;
    this.interactive = false;
    this._drag = null;
    this._suppress = false;
    this._dirty = true;
    this._baseline = null;
    this._previewOrder = null;
    this._pulse = 0;
    this._offs = [];

    this._build();
    this._bind();
    this.refresh();
  }

  /* ── DOM ─────────────────────────────────────────────────────────────── */
  _build() {
    const el = this.el = h('div', 'ax-fb');

    this.head = h('div', 'ax-fb-head',
      h('span', 'ax-fb-caret', '▸'),
      h('span', 'ax-fb-title'),
      h('span', 'ax-fb-rule'),
      h('span', 'ax-fb-live', h('span', 'ax-fb-live-dot'), h('span', null, '')),
    );
    this.titleEl = this.head.querySelector('.ax-fb-title');
    this.liveEl = this.head.querySelector('.ax-fb-live span:last-child');

    this.exprEl = h('div', 'ax-fb-expr');
    this.trackEl = h('div', 'ax-fb-track');

    this.dmgNumEl = h('span', 'ax-fb-dmg-n', '—');
    this.signEl = h('span', 'ax-fb-sign');
    this.dmgEl = h('div', 'ax-fb-dmg', this.dmgNumEl, this.signEl);
    this.deltaEl = h('div', 'ax-fb-delta');
    this.rateEl = h('div', 'ax-fb-rate');
    this.readout = h('div', 'ax-fb-readout',
      h('div', 'ax-fb-readout-label', ''),
      this.dmgEl, this.deltaEl, this.rateEl);
    this.readoutLabel = this.readout.querySelector('.ax-fb-readout-label');

    this.hintEl = h('div', 'ax-fb-hint');

    add(el, [
      h('div', 'ax-fb-frame',
        h('span', 'ax-fb-corner tl'), h('span', 'ax-fb-corner tr'),
        h('span', 'ax-fb-corner bl'), h('span', 'ax-fb-corner br'),
        this.head,
        this.exprEl,
        h('div', 'ax-fb-lower', this.trackEl, this.readout),
        this.hintEl),
    ]);
    this.root.appendChild(el);
    this._localize();
  }

  _localize() {
    this.titleEl.textContent = t('formula.title');
    this.liveEl.textContent = t('formula.live');
    this.readoutLabel.textContent = t('formula.expected');
    this.hintEl.textContent = t('formula.dragHint') + '   ·   ' + t('formula.dragHint2');
  }

  _bind() {
    const on = (e, fn) => this._offs.push(bus.on(e, fn));
    on('formula:changed', () => { if (!this._suppress) this.refresh(true); });
    on('loot:pickup', () => this.refresh(true));
    on('perk:acquired', () => this.refresh(true));
    on('state:phase', ({ phase }) => {
      this.setInteractive(phase === 'forge' || phase === 'draft' || phase === 'menu');
      this.el.classList.toggle('is-dim', phase === 'menu' || phase === 'dead');
    });
    on('input:pointerlock', (locked) => {
      if (S.state && (S.state.phase === 'forge' || S.state.phase === 'draft')) return;
      this.setInteractive(!locked);
    });
    this._offs.push(onLangChange(() => { this._localize(); this.refresh(true); }));

    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onKey = this._onKey.bind(this);
    window.addEventListener('keydown', this._onKey);
  }

  setInteractive(v) {
    if (this.interactive === v) return;
    this.interactive = v;
    this.el.classList.toggle('is-interactive', v);
    if (!v && this._drag) this._cancelDrag();
  }

  /* ── data ─────────────────────────────────────────────────────────────── */

  get formula() { return S.formula || null; }

  _tokens() {
    const f = this.formula;
    if (!f || !Array.isArray(f.slots)) return [];
    const cap = f.capacity ?? f.slots.length;
    const out = f.slots.slice(0, Math.max(cap, f.slots.length));
    while (out.length < cap) out.push(null);
    return out;
  }

  _ctx() {
    return {
      time: S.time?.elapsed ?? 0,
      dt: 1 / 60,
      state: S.state ?? null,
      recentHits: [],
      chain: S.state?.chain ?? 0,
      fireRate: 1,
      perks: S.state?.perkSet ?? new Set(),
      rand,
      meta: {},
      preview: true,
    };
  }

  /** Evaluate a hypothetical slot ordering without emitting formula:changed. */
  _evaluateOrder(order) {
    const f = this.formula;
    if (!f || typeof f.evaluate !== 'function') return null;
    if (typeof f.evaluatePreview === 'function') {
      try { return f.evaluatePreview(order, this._ctx()); } catch { /* fall through */ }
    }
    const snap = f.slots.slice();
    this._suppress = true;
    try {
      for (let i = 0; i < order.length; i++) f.slots[i] = order[i] ?? null;
      return f.evaluate(this._ctx());
    } catch (err) {
      console.warn('[FormulaBar] preview evaluate failed', err);
      return null;
    } finally {
      f.slots.length = snap.length;
      for (let i = 0; i < snap.length; i++) f.slots[i] = snap[i];
      this._suppress = false;
    }
  }

  /* ── rendering ────────────────────────────────────────────────────────── */

  /** Rebuild everything. `flash` pulses the frame to acknowledge a change. */
  refresh(flash = false) {
    const tokens = this._previewOrder || this._tokens();
    this._renderExpr(tokens);
    if (!this._drag) this._renderChips(this._tokens());
    this._renderReadout(tokens, !!this._previewOrder);
    if (flash) this._pulse = 1;
  }

  _renderExpr(tokens) {
    const filled = tokens.filter(Boolean);
    const node = buildExpression(filled, { chain: S.state?.chain ?? 0 });
    this.exprEl.replaceChildren(node);
    // rAF is throttled while the tab is hidden, so remember that the measure
    // pass still owes us and retry from update() once we have real geometry
    this._needLayout = true;
    // one measure/write pass for stretchy delimiters
    requestAnimationFrame(() => stretchDelims(this.exprEl));
    // scale down if the expression outgrows the bar, then publish the bar's
    // final height so anything anchored beneath it (boss bar) can clear it
    requestAnimationFrame(() => {
      const w = node.getBoundingClientRect().width;
      const avail = this.exprEl.clientWidth - 8;
      const k = w > avail && avail > 0 ? Math.max(0.42, avail / w) : 1;
      this.exprEl.style.setProperty('--exprScale', k.toFixed(3));
      if (avail > 0) this._needLayout = false;
      requestAnimationFrame(() => this._publishHeight());
    });
  }

  /** Publish the bar's rendered height as `--fbH` on its host layer. */
  _publishHeight() {
    const host = this.root && this.root.style ? this.root : document.documentElement;
    const hgt = this.el.offsetHeight;
    if (hgt && hgt !== this._lastH) {
      this._lastH = hgt;
      host.style.setProperty('--fbH', `${Math.round(hgt + this.el.offsetTop)}px`);
    }
  }

  _renderChips(tokens) {
    const frag = document.createDocumentFragment();
    this.chips = [];
    tokens.forEach((tok, i) => {
      const chip = this._makeChip(tok, i);
      this.chips.push(chip);
      frag.appendChild(chip);
    });
    this.trackEl.replaceChildren(frag);
  }

  _makeChip(tok, i) {
    const meta = opMeta(tok);
    const empty = !tok;
    const chip = h('div', `ax-chip${empty ? ' is-empty' : ''}`);
    chip.dataset.i = String(i);
    chip.dataset.tier = String(meta.tier);
    chip.tabIndex = empty ? -1 : 0;
    chip.setAttribute('role', 'button');
    chip.setAttribute('aria-label', `${t('formula.slot', { n: i + 1 })} ${meta.label}`);

    if (empty) {
      add(chip, [h('span', 'ax-chip-empty', '∅')]);
      return chip;
    }

    const gfn = CHIP_GLYPH[meta.id];
    add(chip, [
      h('span', 'ax-chip-tier', TIER_ROMAN[meta.tier] || 'I'),
      h('span', 'ax-chip-glyph', gfn ? gfn(tok.value, tok) : mvar('?')),
      h('span', 'ax-chip-idx', String(i + 1)),
      h('span', 'ax-chip-grip', svgEl('svg', { viewBox: '0 0 8 12', class: 'ax-grip-svg' },
        ...[0, 1, 2].flatMap((r) => [0, 1].map((c) =>
          svgEl('circle', { cx: 1.5 + c * 5, cy: 2 + r * 4, r: '0.9', fill: 'currentColor' }))))),
      h('span', 'ax-chip-glow'),
    ]);

    chip.addEventListener('pointerdown', (e) => this._onDown(e, i));
    chip.addEventListener('pointerenter', () => this._highlight(i, true));
    chip.addEventListener('pointerleave', () => this._highlight(i, false));
    chip.addEventListener('focus', () => this._highlight(i, true));
    chip.addEventListener('blur', () => this._highlight(i, false));
    return chip;
  }

  _highlight(i, on) {
    const n = this.exprEl.querySelector(`[data-slot="${i}"]`);
    if (n) n.classList.toggle('mm-hot', on);
    this.chips?.[i]?.classList.toggle('is-hot', on);
  }

  _renderReadout(order, previewing) {
    const res = this._evaluateOrder(order.filter(Boolean).length ? order : this._tokens());
    const dmg = res?.damage ?? null;
    this.dmgNumEl.textContent = dmg != null ? fmt(dmg) : '—';
    this.dmgEl.classList.toggle('is-prime', !!res?.isPrime);
    this._renderSign(res);

    const f = this.formula;
    const rate = typeof f?.fireRate === 'function' ? f.fireRate() : 1;
    // The readout is a *band*, not a single number — a shot draws inside it. Say
    // so, otherwise the damage popups look like they are disagreeing with the
    // number the player was promised here.
    let spread = 0;
    try { spread = f?.baseBand ? f.baseBand().spread : 0; } catch { spread = 0; }
    const band = spread > 0 ? ` ±${Math.round(spread * 100)}%` : '';
    this.rateEl.textContent = `${t('formula.rate')} ×${(rate || 1).toFixed(2)}${band}`;
    this.rateEl.classList.toggle('is-bad', rate < 0.85);

    this.deltaEl.classList.remove('is-up', 'is-down', 'is-flat', 'is-sign');
    if (previewing && this._baseline && res) {
      const v = previewVerdict(this._baseline, signSnapshot(res));
      if (v.sign) {
        const [main, sub] = signCopy(v.sign);
        this.deltaEl.replaceChildren(
          h('span', 'ax-fb-sgn-g', SIGN_COPY[v.sign].glyph),
          h('span', 'ax-fb-sgn-m', main),
          h('span', 'ax-fb-sgn-s', sub));
        this.deltaEl.classList.add('is-sign');
      } else {
        this.deltaEl.textContent = v.text ?? t('formula.noChange');
      }
      this.deltaEl.classList.add(v.cls, 'is-on');
    } else {
      this.deltaEl.classList.remove('is-on');
      this.deltaEl.textContent = '';
    }
    this.el.classList.toggle('is-previewing', previewing);
    this.readoutLabel.textContent = previewing ? t('formula.previewing') : t('formula.expected');
  }

  /** What the shot's sign IS right now, as opposed to what a drag would do to
   *  it. Silent when the build carries no signs at all — most do not, and a
   *  permanently lit ⊕ would stop meaning anything long before wave 20. */
  _renderSign(res) {
    const f = res && res.flags;
    const parity = (f && f.signParity) | 0;
    const pairs = (f && f.parityPairs) | 0;
    const el = this.signEl;
    el.classList.toggle('is-odd', parity === 1);
    el.classList.toggle('is-paired', parity === 0 && pairs > 0);
    if (parity === 1) {
      el.textContent = SIGN_COPY.odd.glyph;
    } else if (pairs > 0) {
      el.textContent = pairs > 1 ? `${SIGN_COPY.paired.glyph}${pairs}` : SIGN_COPY.paired.glyph;
    } else {
      el.textContent = '';
      el.removeAttribute('title');
      return;
    }
    const [main, sub] = signCopy(parity === 1 ? 'odd' : 'paired');
    el.title = `${main} / ${sub}`;
  }

  /* ── drag & drop ──────────────────────────────────────────────────────── */

  _onDown(e, i) {
    if (!this.interactive || e.button !== 0 || this._drag) return;
    const tokens = this._tokens();
    if (!tokens[i]) return;
    e.preventDefault();
    this._drag = {
      i, from: i, to: i, x0: e.clientX, y0: e.clientY,
      x: e.clientX, y: e.clientY, active: false, ghost: null, id: e.pointerId,
    };
    window.addEventListener('pointermove', this._onMove);
    window.addEventListener('pointerup', this._onUp);
    window.addEventListener('pointercancel', this._onUp);
  }

  _startDrag() {
    const d = this._drag;
    const chip = this.chips[d.i];
    const r = chip.getBoundingClientRect();
    d.active = true;
    d.rect = r;
    d.offX = d.x0 - r.left;
    d.offY = d.y0 - r.top;

    const ghost = chip.cloneNode(true);
    ghost.classList.add('ax-chip-ghost');
    ghost.style.width = `${r.width}px`;
    ghost.style.height = `${r.height}px`;
    ghost.style.left = `${r.left}px`;
    ghost.style.top = `${r.top}px`;
    document.body.appendChild(ghost);
    d.ghost = ghost;

    chip.classList.add('is-hole');
    this.el.classList.add('is-dragging');
    document.body.classList.add('ax-dragging');

    this._baseline = signSnapshot(this._evaluateOrder(this._tokens()));
    bus.emit('sfx', { name: 'ui_grab', volume: 0.5 });
    this._moveGhost();
  }

  _onMove(e) {
    const d = this._drag;
    if (!d) return;
    d.x = e.clientX; d.y = e.clientY;
    if (!d.active) {
      if (Math.hypot(d.x - d.x0, d.y - d.y0) < 4) return;
      this._startDrag();
    }
    this._moveGhost();

    // insertion index = how many non-dragged chips have their centre left of us
    const cx = d.x - d.offX + d.rect.width / 2;
    let idx = 0;
    for (let k = 0; k < this.chips.length; k++) {
      const c = this.chips[k];
      if (c.classList.contains('is-hole')) continue;
      const r = c.getBoundingClientRect();
      if (cx > r.left + r.width / 2) idx++;
    }
    idx = Math.max(0, Math.min(this.chips.length - 1, idx));
    if (idx !== d.to) {
      d.to = idx;
      this._flipTo(idx);
      this._previewOrder = reorder(this._tokens(), d.from, idx);
      this._renderExpr(this._previewOrder.filter(Boolean));
      this._renderReadout(this._previewOrder, true);
      bus.emit('sfx', { name: 'ui_tick', volume: 0.35, pitch: 1 + idx * 0.03 });
    }
  }

  _moveGhost() {
    const d = this._drag;
    if (!d.ghost) return;
    const dx = d.x - d.x0;
    const dy = d.y - d.y0;
    const tilt = Math.max(-4.5, Math.min(4.5, dx * 0.04));
    d.ghost.style.transform =
      `translate3d(${dx}px, ${dy * 0.3 - 22}px, 0) scale(1.1) rotate(${tilt.toFixed(2)}deg)`;
  }

  /** FLIP: reorder the DOM, then animate every chip from where it *was*. */
  _flipTo(target) {
    const d = this._drag;
    const first = new Map();
    for (const c of this.chips) first.set(c, c.getBoundingClientRect().left);

    const hole = this.chips[d.i];
    const others = this.chips.filter((c) => c !== hole);
    others.splice(target, 0, hole);
    for (const c of others) this.trackEl.appendChild(c);

    for (const c of this.chips) {
      const dx = first.get(c) - c.getBoundingClientRect().left;
      if (Math.abs(dx) < 0.5) continue;
      c.style.transition = 'none';
      c.style.transform = `translateX(${dx}px)`;
      // force layout so the transition below actually runs
      void c.offsetWidth;
      c.style.transition = SPRING;
      c.style.transform = '';
    }
  }

  _onUp() {
    const d = this._drag;
    if (!d) return;
    window.removeEventListener('pointermove', this._onMove);
    window.removeEventListener('pointerup', this._onUp);
    window.removeEventListener('pointercancel', this._onUp);

    if (!d.active) { this._drag = null; return; }

    const chip = this.chips[d.i];
    const target = chip.getBoundingClientRect();
    const g = d.ghost;
    g.style.transition = 'transform 320ms cubic-bezier(.16,1.3,.3,1), opacity 200ms 160ms linear';
    g.style.transform = `translate3d(${target.left - d.rect.left}px, ${target.top - d.rect.top}px, 0) scale(1)`;
    g.style.opacity = '0';
    const kill = () => g.remove();
    g.addEventListener('transitionend', kill, { once: true });
    setTimeout(kill, 420);

    chip.classList.remove('is-hole');
    this.el.classList.remove('is-dragging');
    document.body.classList.remove('ax-dragging');

    const { from, to } = d;
    this._drag = null;
    this._previewOrder = null;
    this._baseline = null;

    if (from !== to) {
      this._commitMove(from, to);
      bus.emit('sfx', { name: 'ui_drop', volume: 0.7, pitch: 1.0 });
      this._pulse = 1;
    } else {
      bus.emit('sfx', { name: 'ui_drop', volume: 0.35, pitch: 0.85 });
    }
    this.refresh();
  }

  _cancelDrag() {
    const d = this._drag;
    if (!d) return;
    window.removeEventListener('pointermove', this._onMove);
    window.removeEventListener('pointerup', this._onUp);
    d.ghost?.remove();
    this.chips?.[d.i]?.classList.remove('is-hole');
    this.el.classList.remove('is-dragging');
    document.body.classList.remove('ax-dragging');
    this._drag = null;
    this._previewOrder = null;
    this._baseline = null;
    this.refresh();
  }

  /** A move is a rotation, not a swap. Use moveSlot when the Formula offers it,
   *  otherwise decompose into adjacent swaps (which compose to the same thing). */
  _commitMove(from, to) {
    const f = this.formula;
    if (!f) return;
    try {
      if (typeof f.moveSlot === 'function') { f.moveSlot(from, to); return; }
      if (typeof f.swapSlots !== 'function') {
        const next = reorder(this._tokens(), from, to);
        for (let i = 0; i < next.length; i++) f.slots[i] = next[i];
        bus.emit('formula:changed', { formula: f });
        return;
      }
      this._suppress = true;
      if (from < to) for (let i = from; i < to; i++) f.swapSlots(i, i + 1);
      else for (let i = from; i > to; i--) f.swapSlots(i, i - 1);
    } finally {
      this._suppress = false;
    }
    bus.emit('formula:changed', { formula: f });
  }

  /* keyboard reorder: focus a chip, Shift+←/→ moves it */
  _onKey(e) {
    if (!this.interactive) return;
    const el = document.activeElement;
    if (!el || !el.classList?.contains('ax-chip')) return;
    const i = Number(el.dataset.i);
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      const j = Math.max(0, Math.min(this.chips.length - 1, i + dir));
      if (j === i) return;
      e.preventDefault();
      if (e.shiftKey) {
        this._commitMove(i, j);
        this.refresh(true);
        requestAnimationFrame(() => this.chips[j]?.focus());
      } else {
        this.chips[j]?.focus();
      }
    }
  }

  /* ── frame ────────────────────────────────────────────────────────────── */
  update(dt) {
    if (this._pulse > 0) {
      this._pulse = Math.max(0, this._pulse - dt * 2.6);
      this.el.style.setProperty('--fbPulse', this._pulse.toFixed(3));
    }
    if (this._needLayout && this.exprEl.clientWidth > 0) {
      const node = this.exprEl.firstElementChild;
      if (node) {
        stretchDelims(this.exprEl);
        const w = node.getBoundingClientRect().width;
        const avail = this.exprEl.clientWidth - 8;
        const k = w > avail ? Math.max(0.42, avail / w) : 1;
        this.exprEl.style.setProperty('--exprScale', k.toFixed(3));
        this._publishHeight();
      }
      this._needLayout = false;
    }
  }

  dispose() {
    for (const off of this._offs) off();
    window.removeEventListener('keydown', this._onKey);
    this._cancelDrag();
    this.el.remove();
  }
}

/* ── utils ──────────────────────────────────────────────────────────────── */

export function reorder(arr, from, to) {
  const a = arr.slice();
  const [x] = a.splice(from, 1);
  a.splice(to, 0, x);
  return a;
}

const SUP_MAP = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '.': '·', '-': '⁻' };
export function supDigits(s) { return String(s).split('').map((c) => SUP_MAP[c] || c).join(''); }

/**
 * Light inline maths for prose (perk descriptions, tooltips): turns
 * `x^{2}`, `log_{2}`, `x^2` into real sup/sub runs. Returns a DocumentFragment.
 * Deliberately tiny — this is typography polish, not a parser.
 */
export function inlineMath(text) {
  const frag = document.createDocumentFragment();
  const src = String(text ?? '');
  const re = /([_^])(?:\{([^}]*)\}|(\S))/g;
  let last = 0, m;
  while ((m = re.exec(src))) {
    if (m.index > last) frag.appendChild(document.createTextNode(src.slice(last, m.index)));
    const body = m[2] !== undefined ? m[2] : m[3];
    frag.appendChild(h('span', m[1] === '^' ? 'mm-inline-sup' : 'mm-inline-sub', body));
    last = re.lastIndex;
  }
  if (last < src.length) frag.appendChild(document.createTextNode(src.slice(last)));
  return frag;
}

export default FormulaBar;
